import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  RefreshControl,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { FormHeader } from '../components/FormHeader';
import { useGoBack } from '../hooks/useGoBack';
import { MoreOptionsModal } from '../components/MoreOptionsModal';
import { DatePickerField } from '../components/DatePickerField';
import { apiService } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { firstText } from '../utils/contactIdentifiers';

interface Task {
  _id: string;
  name: string;
  description?: string;
  type: 'scheduled' | 'onDemand';
  scope: 'system' | 'business';
  handler: string;
  status: number;
  executionCount: number;
  failureCount: number;
  lastExecutedAt?: string;
  nextExecutionAt?: string;
  cronExpression?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  parameters?: any;
  isExecuted?: boolean;
  executionStatus?: 'pending' | 'executed' | 'scheduled' | 'failed';
}

interface ExecutionLog {
  _id: string;
  taskId: string;
  status: 'running' | 'success' | 'failed';
  startedAt: string;
  completedAt?: string;
  duration?: number;
  result?: any;
  error?: string;
  retryAttempt: number;
}

interface ContactStatusEntry {
  contactId: string;
  name?: string;
  phoneNumber?: string;
  error?: string;
}

interface MessageStats {
  totalSent: number;
  failed: number;
  sent: number;
  delivered: number;
  read: number;
  pending: number;
  billableMessages: number;
  successRate: number;
  failureRate: number;
  contactsByStatus?: {
    failed?: ContactStatusEntry[];
    pending?: ContactStatusEntry[];
    sent?: ContactStatusEntry[];
    delivered?: ContactStatusEntry[];
    read?: ContactStatusEntry[];
    replied?: ContactStatusEntry[];
    handled?: ContactStatusEntry[];
  };
  breakdown: {
    failed: number;
    sentButNotDelivered: number;
    sent: number;
    delivered: number;
    read: number;
    pending: number;
  };
  /** Set when the campaign was paused/aborted mid-run and some contacts were
   * never attempted at all, distinct from per-contact send failures below. */
  pausedReason?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  failed:    { label: 'Failed',    color: '#EF4444', icon: 'close-circle'       },
  pending:   { label: 'Pending',   color: '#F59E0B', icon: 'time-outline'        },
  sent:      { label: 'Sent',      color: '#3B82F6', icon: 'checkmark-outline'   },
  delivered: { label: 'Delivered', color: '#8B5CF6', icon: 'checkmark-done'      },
  read:      { label: 'Read',      color: '#10B981', icon: 'eye-outline'         },
  replied:   { label: 'Replied',   color: '#06B6D4', icon: 'chatbubble-outline'  },
  handled:   { label: 'Handled',   color: '#6B7280', icon: 'shield-checkmark-outline' },
};

export const TaskHistoryScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { businessRole, currentBusiness } = useAppStore();
  const { showNotification, navigateTo, setActiveTab, activeTab } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const goBack = useGoBack('home');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [executionHistory, setExecutionHistory] = useState<ExecutionLog[]>([]);
  const [taskMessageStats, setTaskMessageStats] = useState<Record<string, MessageStats>>({});
  const [selectedTaskMessageStats, setSelectedTaskMessageStats] = useState<MessageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);

  // Filters — status is multi-select (empty set = no status restriction, same as 'all').
  const [statusFilter, setStatusFilter] = useState<Set<'success' | 'failed' | 'pending'>>(new Set());
  const [createdByFilter, setCreatedByFilter] = useState<Set<string>>(new Set());
  const [contactFilter, setContactFilter] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [businessUsers, setBusinessUsers] = useState<{ id: string; name: string }[]>([]);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showCreatedByPicker, setShowCreatedByPicker] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // Contact review selection
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [activeStatusTab, setActiveStatusTab] = useState<string>('all');
  const [expandedErrorContactIds, setExpandedErrorContactIds] = useState<Set<string>>(new Set());

  // Action modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Right pane / modal
  const [showRightPane] = useState(true);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);

  const isAdminOrOwner = businessRole === 'admin' || businessRole === 'owner';
  const canView = isAdminOrOwner || hasPermissionAtLeast('task.viewHistory', 'view');
  // Resend follows the same visibility scope as viewing, not a separate edit tier — any
  // user who can see a task (own for View, own+team for Manager, all for Admin) can also
  // resend it. The backend's canAccessTask still enforces per-task ownership.
  const canResend = canView;
  // True View tier (not Manager/Admin) only ever sees their own tasks, so "Sent by" is a
  // no-op filter for them — hide it rather than showing a picker with a single, forced entry.
  const isViewTierOnly = canView && !isAdminOrOwner && !hasPermissionAtLeast('task.viewHistory', 'edit');

  // Multi-select for bulk resend
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [bulkResending, setBulkResending] = useState(false);

  useEffect(() => {
    setActiveTab('more');
    if (canView) loadTasks();
  }, [canView, setActiveTab]);

  useEffect(() => {
    if (!canView || isViewTierOnly || !currentBusiness?._id) return;
    apiService.getBusinessUsers(currentBusiness._id).then(r => {
      if (r.statusCode === 1000 && r.data) {
        setBusinessUsers(r.data.map(bu => ({
          id: bu.userId,
          name: [bu.user?.firstName, bu.user?.lastName].filter(Boolean).join(' ') || bu.user?.email || bu.userId,
        })));
      }
    }).catch(() => { /* ignore */ });
  }, [canView, isViewTierOnly, currentBusiness?._id]);

  const loadTasks = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiService.getBusinessTasks();
      if (response.statusCode === 1000 && response.data) {
        const whatsappTasks = (response.data as Task[])
          .filter(t => t.handler === 'whatsapp:send-bulk-messages')
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setTasks(whatsappTasks);

        // Fetched per-task and merged into state as each one resolves (not batched into
        // one setTaskMessageStats after every task finishes) so cards show their stats
        // (sent/failed/billable) as soon as they're available instead of all of them
        // waiting on the slowest task's request before any card gets to display anything.
        await Promise.all(
          whatsappTasks.map(async (task) => {
            try {
              const r = await apiService.getTaskMessageStats(task._id);
              if (r.statusCode === 1000 && r.data) {
                setTaskMessageStats(prev => ({ ...prev, [task._id]: r.data as MessageStats }));
              }
            } catch { /* ignore per-task errors */ }
          })
        );
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to load tasks');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const loadExecutionHistory = useCallback(async (taskId: string) => {
    try {
      const r = await apiService.getTaskExecutionHistory(taskId, 50);
      if (r.statusCode === 1000 && r.data) setExecutionHistory(r.data as ExecutionLog[]);
    } catch { /* ignore */ }
  }, []);

  const loadMessageStats = useCallback(async (taskId: string) => {
    if (taskMessageStats[taskId]) {
      setSelectedTaskMessageStats(taskMessageStats[taskId]);
      return;
    }
    try {
      const r = await apiService.getTaskMessageStats(taskId);
      if (r.statusCode === 1000 && r.data) {
        const stats = r.data as MessageStats;
        setSelectedTaskMessageStats(stats);
        setTaskMessageStats(prev => ({ ...prev, [taskId]: stats }));
      }
    } catch { /* ignore */ }
  }, [taskMessageStats]);

  const handleTaskPress = useCallback(async (task: Task) => {
    setSelectedTask(task);
    setLoadingTaskDetails(true);
    setSelectedContacts(new Set());
    setActiveStatusTab('all');

    if (Platform.OS === 'web') {
      const newPath = `/task-history/${task._id}`;
      if (window.location.pathname !== newPath) {
        window.history.pushState({ taskId: task._id }, '', newPath);
      }
    } else {
      setShowExecutionModal(true);
    }

    try {
      await Promise.all([loadExecutionHistory(task._id), loadMessageStats(task._id)]);
    } finally {
      setLoadingTaskDetails(false);
    }
  }, [loadExecutionHistory, loadMessageStats]);

  // Auto-select from URL / pending storage
  useEffect(() => {
    if (tasks.length === 0 || !isAdminOrOwner) return;
    const selectById = (id: string) => {
      const task = tasks.find(t => t._id === id);
      if (task && (!selectedTask || selectedTask._id !== id)) {
        setTimeout(() => handleTaskPress(task), 300);
        return true;
      }
      return false;
    };
    const check = async () => {
      await new Promise(r => setTimeout(r, 200));
      if (Platform.OS === 'web') {
        const m = window.location.pathname.match(/\/task-history\/([^/]+)/);
        if (m?.[1]) { selectById(m[1]); return; }
      }
      try {
        const { getPendingTaskId } = await import('../utils/tempStorage');
        const id = await getPendingTaskId();
        if (id) selectById(id);
      } catch { /* ignore */ }
    };
    check();
  }, [tasks, selectedTask, handleTaskPress, isAdminOrOwner]);

  // Browser back/forward
  useEffect(() => {
    if (Platform.OS !== 'web' || !isAdminOrOwner) return;
    const onPop = () => {
      const m = window.location.pathname.match(/\/task-history\/([^/]+)/);
      if (m?.[1]) {
        const task = tasks.find(t => t._id === m[1]);
        if (task && (!selectedTask || selectedTask._id !== m[1])) handleTaskPress(task);
      } else if (selectedTask) {
        setSelectedTask(null); setExecutionHistory([]); setSelectedTaskMessageStats(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [tasks, selectedTask, handleTaskPress, isAdminOrOwner]);

  const handleCloseRightPane = useCallback(() => {
    setSelectedTask(null); setExecutionHistory([]); setSelectedTaskMessageStats(null);
    setLoadingTaskDetails(false); setSelectedContacts(new Set());
    if (Platform.OS !== 'web') setShowExecutionModal(false);
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadTasks();
    setRefreshing(false);
  }, [loadTasks]);

  const handleTabPress = useCallback(
    createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) }),
    [navigateTo, setActiveTab]
  );

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const formatDate = (s?: string) => s ? new Date(s).toLocaleString() : 'N/A';

  const isTaskScheduledNotExecuted = (t: Task) =>
    !!(t.nextExecutionAt && new Date(t.nextExecutionAt) > new Date() && !t.lastExecutedAt && t.executionCount === 0);

  const isTaskExecuted = (t: Task) => !!(t.lastExecutedAt || t.executionCount > 0);

  const isTaskScheduled = (t: Task) => !!(t.nextExecutionAt && new Date(t.nextExecutionAt) > new Date());

  /** A task with failureCount === 0 still isn't a real success if it never actually sent
   * anything (e.g. every contact was skipped for having no valid number/location) — that's
   * a silent failure, not a clean run, so it's treated as failed for status/filtering. */
  const isTaskEffectivelyFailed = (t: Task): boolean => {
    if (t.failureCount > 0) return true;
    const stats = taskMessageStats[t._id];
    if (isTaskExecuted(t) && stats && stats.totalSent === 0) return true;
    return false;
  };

  const getTaskStatusColor = (t: Task) => {
    if (t.status === 0) return colors.text.secondary;
    if (t.status === 2) return colors.warning;
    if (t.status === 3) return colors.error;
    if (isTaskEffectivelyFailed(t)) return colors.warning;
    return colors.success;
  };

  const getTaskExecutionStatus = (t: Task): { text: string; isExecuted: boolean; isScheduled: boolean } => {
    const executed = isTaskExecuted(t);
    const scheduled = isTaskScheduled(t);
    if (t.status === 0) return { text: 'Deleted', isExecuted: executed, isScheduled: scheduled };
    if (t.status === 2) return { text: 'Paused', isExecuted: executed, isScheduled: scheduled };
    if (t.status === 3) return { text: 'Failed', isExecuted: executed, isScheduled: scheduled };
    if (scheduled) {
      const scheduleText = `Scheduled: ${formatDate(t.nextExecutionAt)}`;
      return { text: executed ? scheduleText : scheduleText + ' (not run yet)', isExecuted: executed, isScheduled: scheduled };
    }
    if (executed) {
      const stats = taskMessageStats[t._id];
      if (t.failureCount === 0 && stats && stats.totalSent === 0) return { text: 'Executed (0 sent)', isExecuted: true, isScheduled: false };
      if (t.failureCount > 0 && t.failureCount === t.executionCount) return { text: `Executed (${t.failureCount} failed)`, isExecuted: true, isScheduled: false };
      if (t.failureCount > 0) return { text: `Executed (${t.executionCount - t.failureCount} success, ${t.failureCount} failed)`, isExecuted: true, isScheduled: false };
      return { text: `Executed (${t.executionCount} success)`, isExecuted: true, isScheduled: false };
    }
    return { text: 'Pending execution', isExecuted: false, isScheduled: false };
  };

  // ─── Task Actions ────────────────────────────────────────────────────────────

  const handleStopTask = async () => {
    if (!selectedTask) return;
    setActionLoading(true);
    try {
      const r = await apiService.deleteTask(selectedTask._id);
      if (r.statusCode === 1000) {
        showNotification('success', 'Task stopped', 'The scheduled task has been cancelled.');
        setTasks(prev => prev.filter(t => t._id !== selectedTask._id));
        handleCloseRightPane();
      } else {
        showNotification('error', 'Error', r.errors?.[0]?.message || 'Failed to stop task');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to stop task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedTask || !rescheduleDate) return;
    const date = new Date(rescheduleDate);
    if (isNaN(date.getTime()) || date <= new Date()) {
      showNotification('error', 'Invalid date', 'Please pick a future date and time.');
      return;
    }
    setActionLoading(true);
    try {
      const r = await apiService.updateTask(selectedTask._id, { nextExecutionAt: date.toISOString() });
      if (r.statusCode === 1000) {
        showNotification('success', 'Rescheduled', `Task will run at ${date.toLocaleString()}`);
        setShowRescheduleModal(false);
        setRescheduleDate('');
        await loadTasks();
      } else {
        showNotification('error', 'Error', r.errors?.[0]?.message || 'Failed to reschedule task');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to reschedule task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloneMessage = () => {
    if (!selectedTask?.parameters) return;
    const { templateName, templateLanguage, message, parameters: params, providerId, headerAsset } = selectedTask.parameters;
    navigateTo('send-whatsapp-message', undefined, {
      cloneParams: { templateName, templateLanguage, message, parameters: params, providerId, headerAsset },
    });
  };

  const handleCloneMessageAndContacts = () => {
    if (!selectedTask?.parameters) return;
    const { contacts, templateName, templateLanguage, message, parameters: params, providerId, headerAsset } = selectedTask.parameters;
    const bulkContacts = (contacts || []).map((c: any) => ({
      name: c.name || '',
      phoneNumber: c.phoneNumber || c.phone || '',
    }));
    navigateTo('send-whatsapp-message', undefined, {
      bulkContacts,
      cloneParams: { templateName, templateLanguage, message, parameters: params, providerId, headerAsset },
    });
  };

  const handleResend = async () => {
    if (!selectedTask) return;
    setActionLoading(true);
    try {
      const r = await apiService.executeTask(selectedTask._id);
      if (r.statusCode === 1000) {
        showNotification('success', 'Resent', 'Task is running again — check execution history for the new run.');
        await Promise.all([loadExecutionHistory(selectedTask._id), loadTasks()]);
        const refreshed = await apiService.getTaskMessageStats(selectedTask._id);
        if (refreshed.statusCode === 1000 && refreshed.data) {
          const stats = refreshed.data as MessageStats;
          setSelectedTaskMessageStats(stats);
          setTaskMessageStats(prev => ({ ...prev, [selectedTask._id]: stats }));
        }
      } else {
        showNotification('error', 'Error', r.errors?.[0]?.message || 'Failed to resend task');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to resend task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkResend = async () => {
    if (selectedTaskIds.size === 0) return;
    setBulkResending(true);
    try {
      const ids = Array.from(selectedTaskIds);
      const results = await Promise.all(ids.map(id => apiService.executeTask(id).catch((e: any) => ({ statusCode: 0, errors: [{ message: e.message }] }))));
      const failed = results.filter(r => r.statusCode !== 1000).length;
      if (failed === 0) {
        showNotification('success', 'Resent', `${ids.length} task(s) resent successfully.`);
      } else {
        showNotification('error', 'Partial failure', `${ids.length - failed} succeeded, ${failed} failed to resend.`);
      }
      setSelectionMode(false);
      setSelectedTaskIds(new Set());
      await loadTasks();
    } finally {
      setBulkResending(false);
    }
  };

  const toggleTaskSelect = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };

  const handleSendToSelected = () => {
    if (selectedContacts.size === 0) return;
    const allContacts = getAllContactsFlat();
    const bulk = allContacts
      .filter(c => selectedContacts.has(c.contactId))
      .map(c => ({ name: c.name || '', phoneNumber: c.phoneNumber || '' }));
    if (bulk.length === 0) return;
    navigateTo('send-whatsapp-message', undefined, { bulkContacts: bulk });
  };

  // ─── Contact review helpers ─────────────────────────────────────────────────

  const getAllContactsFlat = (): ContactStatusEntry[] => {
    if (!selectedTaskMessageStats?.contactsByStatus) return [];
    const seen = new Set<string>();
    const all: ContactStatusEntry[] = [];
    Object.values(selectedTaskMessageStats.contactsByStatus).forEach(arr => {
      arr?.forEach(c => {
        if (!seen.has(c.contactId)) { seen.add(c.contactId); all.push(c); }
      });
    });
    return all;
  };

  const getContactStatus = (contactId: string): string => {
    const cbs = selectedTaskMessageStats?.contactsByStatus;
    if (!cbs) return 'unknown';
    for (const [status, arr] of Object.entries(cbs)) {
      if (arr?.some(c => c.contactId === contactId)) return status;
    }
    return 'unknown';
  };

  const getContactsForActiveTab = (): ContactStatusEntry[] => {
    const cbs = selectedTaskMessageStats?.contactsByStatus;
    if (!cbs) return [];
    if (activeStatusTab === 'all') return getAllContactsFlat();
    return cbs[activeStatusTab] || [];
  };

  const toggleContact = (contactId: string) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const toggleErrorExpanded = (contactId: string) => {
    setExpandedErrorContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visible = getContactsForActiveTab();
    const allSelected = visible.every(c => selectedContacts.has(c.contactId));
    if (allSelected) {
      setSelectedContacts(prev => {
        const next = new Set(prev);
        visible.forEach(c => next.delete(c.contactId));
        return next;
      });
    } else {
      setSelectedContacts(prev => {
        const next = new Set(prev);
        visible.forEach(c => next.add(c.contactId));
        return next;
      });
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────────────────

  const renderStatusPills = (stats: MessageStats) => {
    const pills = [
      { key: 'totalSent',  label: 'Total',     value: stats.totalSent,        color: colors.primary       },
      { key: 'failed',     label: 'Failed',    value: stats.failed,           color: '#EF4444'            },
      { key: 'delivered',  label: 'Delivered', value: stats.delivered,        color: '#8B5CF6'            },
      { key: 'read',       label: 'Read',      value: stats.read,             color: '#10B981'            },
      { key: 'pending',    label: 'Pending',   value: stats.pending,          color: '#F59E0B'            },
      { key: 'billable',   label: 'Billable',  value: stats.billableMessages, color: colors.primary       },
    ];
    return (
      <View style={styles.pillsRow}>
        {pills.map(p => (
          <View key={p.key} style={[styles.statPill, { borderColor: p.color }]}>
            <Text style={[styles.statPillValue, { color: p.color }]}>{p.value}</Text>
            <Text style={styles.statPillLabel}>{p.label}</Text>
          </View>
        ))}
      </View>
    );
  };

  const renderContactReview = () => {
    const cbs = selectedTaskMessageStats?.contactsByStatus;
    if (!cbs) return null;
    const statusKeys = Object.keys(STATUS_CONFIG).filter(k => (cbs[k as keyof typeof cbs]?.length ?? 0) > 0);
    if (statusKeys.length === 0) return null;

    const visibleContacts = getContactsForActiveTab();
    const allVisibleSelected = visibleContacts.length > 0 && visibleContacts.every(c => selectedContacts.has(c.contactId));

    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="people-outline" size={20} color={colors.primary} />
          <Text style={styles.sectionTitle}>Recipient Status</Text>
        </View>

        {selectedTaskMessageStats?.pausedReason && (
          <View style={styles.pausedBanner}>
            <Ionicons name="warning-outline" size={16} color="#F59E0B" />
            <Text style={styles.pausedBannerText}>
              Campaign paused — some contacts were never attempted: {selectedTaskMessageStats.pausedReason}
            </Text>
          </View>
        )}

        {/* Status tabs */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.statusTabsScroll}>
          <View style={styles.statusTabs}>
            <TouchableOpacity
              style={[styles.statusTab, activeStatusTab === 'all' && styles.statusTabActive]}
              onPress={() => setActiveStatusTab('all')}
            >
              <Text style={[styles.statusTabText, activeStatusTab === 'all' && styles.statusTabTextActive]}>
                All ({getAllContactsFlat().length})
              </Text>
            </TouchableOpacity>
            {statusKeys.map(k => {
              const cfg = STATUS_CONFIG[k];
              const count = cbs[k as keyof typeof cbs]?.length ?? 0;
              return (
                <TouchableOpacity
                  key={k}
                  style={[styles.statusTab, activeStatusTab === k && { ...styles.statusTabActive, borderColor: cfg.color, backgroundColor: cfg.color + '20' }]}
                  onPress={() => setActiveStatusTab(k)}
                >
                  <Ionicons name={cfg.icon as any} size={12} color={activeStatusTab === k ? cfg.color : colors.text.secondary} />
                  <Text style={[styles.statusTabText, activeStatusTab === k && { color: cfg.color, fontFamily: typography.fontFamily.semibold }]}>
                    {cfg.label} ({count})
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>

        {/* Select all + action bar */}
        {visibleContacts.length > 0 && (
          <View style={styles.contactActionBar}>
            <TouchableOpacity style={styles.selectAllRow} onPress={toggleSelectAll}>
              <View style={[styles.checkbox, allVisibleSelected && styles.checkboxChecked]}>
                {allVisibleSelected && <Ionicons name="checkmark" size={12} color={colors.white} />}
              </View>
              <Text style={styles.selectAllText}>
                {allVisibleSelected ? 'Deselect all' : 'Select all'} ({visibleContacts.length})
              </Text>
            </TouchableOpacity>
            {selectedContacts.size > 0 && (
              <TouchableOpacity style={styles.sendSelectedBtn} onPress={handleSendToSelected}>
                <Ionicons name="logo-whatsapp" size={14} color={colors.white} />
                <Text style={styles.sendSelectedBtnText}>Message {selectedContacts.size}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Contact list */}
        <View style={styles.contactList}>
          {visibleContacts.map(c => {
            const status = activeStatusTab === 'all' ? getContactStatus(c.contactId) : activeStatusTab;
            const cfg = STATUS_CONFIG[status] || { color: colors.text.secondary, icon: 'ellipse-outline', label: status };
            const isSelected = selectedContacts.has(c.contactId);
            return (
              <TouchableOpacity
                key={c.contactId}
                style={[styles.contactRow, isSelected && styles.contactRowSelected]}
                onPress={() => toggleContact(c.contactId)}
                activeOpacity={0.7}
              >
                <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
                  {isSelected && <Ionicons name="checkmark" size={12} color={colors.white} />}
                </View>
                <View style={styles.contactInfo}>
                  <Text style={styles.contactName} numberOfLines={1}>
                    {c.name || c.phoneNumber || c.contactId}
                  </Text>
                  {c.phoneNumber && c.name && (
                    <Text style={styles.contactPhone}>{c.phoneNumber}</Text>
                  )}
                  {c.error && (
                    <TouchableOpacity
                      style={styles.errorContainer}
                      onPress={(e) => { e.stopPropagation(); toggleErrorExpanded(c.contactId); }}
                      activeOpacity={0.6}
                    >
                      <Text
                        style={styles.contactErrorText}
                        numberOfLines={expandedErrorContactIds.has(c.contactId) ? undefined : 2}
                      >
                        {c.error}
                      </Text>
                      {!expandedErrorContactIds.has(c.contactId) && c.error.length > 80 && (
                        <Text style={styles.contactErrorMoreText}>Show more</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
                <View style={[styles.contactStatusBadge, { backgroundColor: cfg.color + '20' }]}>
                  <Ionicons name={cfg.icon as any} size={12} color={cfg.color} />
                  <Text style={[styles.contactStatusText, { color: cfg.color }]}>{cfg.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
          {visibleContacts.length === 0 && (
            <Text style={styles.emptyContactsText}>No contacts in this category</Text>
          )}
        </View>
      </View>
    );
  };

  const renderTaskActions = (task: Task) => {
    const scheduledNotExecuted = isTaskScheduledNotExecuted(task);
    const executed = isTaskExecuted(task);
    const hasContacts = !!(task.parameters?.contacts?.length);

    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="flash-outline" size={20} color={colors.primary} />
          <Text style={styles.sectionTitle}>Actions</Text>
        </View>
        <View style={styles.actionsGrid}>
          {scheduledNotExecuted && (
            <>
              <TouchableOpacity
                style={[styles.actionBtn, styles.actionBtnDanger]}
                onPress={handleStopTask}
                disabled={actionLoading}
              >
                <Ionicons name="stop-circle-outline" size={18} color="#EF4444" />
                <Text style={[styles.actionBtnText, { color: '#EF4444' }]}>Stop Task</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionBtn}
                onPress={() => setShowRescheduleModal(true)}
                disabled={actionLoading}
              >
                <Ionicons name="calendar-outline" size={18} color={colors.primary} />
                <Text style={styles.actionBtnText}>Reschedule</Text>
              </TouchableOpacity>
            </>
          )}

          <TouchableOpacity style={styles.actionBtn} onPress={handleCloneMessage}>
            <Ionicons name="copy-outline" size={18} color={colors.primary} />
            <Text style={styles.actionBtnText}>Clone Message</Text>
          </TouchableOpacity>

          {hasContacts && (
            <TouchableOpacity style={styles.actionBtn} onPress={handleCloneMessageAndContacts}>
              <Ionicons name="duplicate-outline" size={18} color={colors.primary} />
              <Text style={styles.actionBtnText}>Clone + Contacts</Text>
            </TouchableOpacity>
          )}

          {executed && hasContacts && canResend && (
            <TouchableOpacity style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={handleResend} disabled={actionLoading}>
              {actionLoading
                ? <ActivityIndicator size="small" color={colors.white} />
                : <Ionicons name="send-outline" size={18} color={colors.white} />
              }
              <Text style={[styles.actionBtnText, { color: colors.white }]}>Resend</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderTaskInfo = (task: Task) => {
    const statusInfo = getTaskExecutionStatus(task);
    const statusColor = getTaskStatusColor(task);
    const params = task.parameters as any;

    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="document-text-outline" size={20} color={colors.primary} />
          <Text style={styles.sectionTitle}>Task Details</Text>
        </View>
        <View style={styles.infoGrid}>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Status</Text>
            <Text style={[styles.infoValue, { color: statusColor }]}>{statusInfo.text}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Type</Text>
            <Text style={styles.infoValue}>{task.type === 'onDemand' ? 'Immediate' : 'Scheduled'}</Text>
          </View>
          {task.lastExecutedAt && (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Last Executed</Text>
              <Text style={styles.infoValue}>{formatDate(task.lastExecutedAt)}</Text>
            </View>
          )}
          {task.nextExecutionAt && (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Next Execution</Text>
              <Text style={styles.infoValue}>{formatDate(task.nextExecutionAt)}</Text>
            </View>
          )}
          {task.cronExpression && (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Cron</Text>
              <Text style={styles.infoValue}>{task.cronExpression}</Text>
            </View>
          )}
          {params?.contacts && (
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Contacts</Text>
              <Text style={styles.infoValue}>
                {params.contacts.length}
                {params.limitedContacts ? ' (quota limited)' : ''}
                {params.originalContactCount && params.originalContactCount !== params.contacts.length
                  ? ` of ${params.originalContactCount} requested`
                  : ''}
              </Text>
            </View>
          )}
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Executions</Text>
            <Text style={styles.infoValue}>{task.executionCount} total, {task.failureCount} failed</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Created</Text>
            <Text style={styles.infoValue}>{formatDate(task.createdAt)}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderExecutionHistory = () => {
    if (executionHistory.length === 0) {
      return (
        <View style={styles.emptyExecutionContainer}>
          <Ionicons name="time-outline" size={40} color={colors.text.secondary} />
          <Text style={styles.emptyExecutionText}>No execution history</Text>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="time-outline" size={20} color={colors.primary} />
          <Text style={styles.sectionTitle}>Execution History</Text>
        </View>
        {executionHistory.map((log, i) => {
          const statusColor = log.status === 'success' ? colors.success : log.status === 'failed' ? colors.error : colors.warning;
          const duration = log.duration ? `${(log.duration / 1000).toFixed(1)}s` : 'N/A';
          return (
            <View key={log._id || i} style={styles.executionLogCard}>
              <View style={styles.executionLogHeader}>
                <View style={[styles.executionStatusDot, { backgroundColor: statusColor }]} />
                <Text style={[styles.executionStatusText, { color: statusColor }]}>
                  {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                </Text>
                {log.retryAttempt > 0 && <Text style={styles.retryText}>(Retry #{log.retryAttempt})</Text>}
                <Text style={styles.executionDuration}>{duration}</Text>
              </View>
              <Text style={styles.executionLogMeta}>
                Started: {formatDate(log.startedAt)}
                {log.completedAt ? `  •  Completed: ${formatDate(log.completedAt)}` : ''}
              </Text>
              {log.error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{log.error}</Text>
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const renderRightPaneContent = () => {
    if (!selectedTask) {
      return (
        <View style={styles.rightPaneEmpty}>
          <Ionicons name="document-text-outline" size={64} color={colors.text.secondary} />
          <Text style={styles.rightPaneEmptyTitle}>Select a Task</Text>
          <Text style={styles.rightPaneEmptyText}>Click on a task to view details.</Text>
        </View>
      );
    }

    if (loadingTaskDetails) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading task details...</Text>
        </View>
      );
    }

    return (
      <>
        {/* Task name header */}
        <View style={styles.detailsHeaderRow}>
          <Text style={styles.detailsTitle} numberOfLines={2}>{selectedTask.name}</Text>
          {selectedTask.description && (
            <Text style={styles.detailsSubtitle} numberOfLines={2}>{selectedTask.description}</Text>
          )}
        </View>

        {/* Actions */}
        {renderTaskActions(selectedTask)}

        {/* Task info */}
        {renderTaskInfo(selectedTask)}

        {/* Message stats */}
        {selectedTaskMessageStats && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Ionicons name="bar-chart-outline" size={20} color={colors.primary} />
              <Text style={styles.sectionTitle}>Message Statistics</Text>
              <View style={styles.successRateBadge}>
                <Text style={styles.successRateText}>{selectedTaskMessageStats.successRate}% success</Text>
              </View>
            </View>
            {renderStatusPills(selectedTaskMessageStats)}
          </View>
        )}

        {/* Contact review */}
        {renderContactReview()}

        {/* Execution history */}
        {renderExecutionHistory()}
      </>
    );
  };

  const renderTaskCard = ({ item: task }: { item: Task }) => {
    const statusInfo = getTaskExecutionStatus(task);
    const statusColor = getTaskStatusColor(task);
    const messageStats = taskMessageStats[task._id];
    const isScheduledNotExecuted = isTaskScheduledNotExecuted(task);
    const isSelected = selectedTask?._id === task._id;

    const totalCount = messageStats ? messageStats.totalSent : task.executionCount;
    const failedCount = messageStats ? messageStats.failed : task.failureCount;
    const successRate = totalCount > 0 ? (((totalCount - failedCount) / totalCount) * 100).toFixed(1) : '0';
    const isChecked = selectedTaskIds.has(task._id);
    const executedForResend = isTaskExecuted(task) && !!(task.parameters?.contacts?.length);

    return (
      <TouchableOpacity
        style={[
          styles.taskCard,
          isScheduledNotExecuted && styles.taskCardScheduled,
          isSelected && styles.taskCardSelected,
        ]}
        onPress={() => selectionMode ? (executedForResend && toggleTaskSelect(task._id)) : handleTaskPress(task)}
        activeOpacity={0.7}
      >
        <View style={styles.taskCardHeader}>
          <View style={styles.taskCardTitleRow}>
            {selectionMode ? (
              <View style={[styles.checkbox, isChecked && styles.checkboxChecked, !executedForResend && styles.checkboxDisabled]}>
                {isChecked && <Ionicons name="checkmark" size={12} color={colors.white} />}
              </View>
            ) : (
              <Ionicons
                name={isScheduledNotExecuted ? 'time-outline' : statusInfo.isExecuted ? 'checkmark-circle-outline' : 'document-text-outline'}
                size={18}
                color={isScheduledNotExecuted ? colors.warning : colors.primary}
              />
            )}
            <Text style={styles.taskCardTitle} numberOfLines={2}>{task.name}</Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: `${String(isScheduledNotExecuted ? colors.warning : statusColor)}20` }]}>
            <Text style={[styles.statusText, { color: isScheduledNotExecuted ? colors.warning : statusColor }]} numberOfLines={1}>
              {isScheduledNotExecuted ? 'Scheduled' : statusInfo.text}
            </Text>
          </View>
        </View>

        {/* Mini stats row */}
        <View style={styles.taskStats}>
          {messageStats ? (
            <>
              <View style={styles.statItem}>
                <Ionicons name="send" size={13} color={colors.primary} />
                <Text style={styles.statText}>{messageStats.totalSent} sent</Text>
              </View>
              <View style={styles.statItem}>
                <Ionicons name="close-circle" size={13} color="#EF4444" />
                <Text style={styles.statText}>{failedCount} failed</Text>
              </View>
              <View style={styles.statItem}>
                <Ionicons name="stats-chart" size={13} color={colors.success} />
                <Text style={styles.statText}>{successRate}%</Text>
              </View>
              {(messageStats.billableMessages ?? 0) > 0 && (
                <View style={styles.statItem}>
                  <Ionicons name="card" size={13} color={colors.primary} />
                  <Text style={styles.statText}>{messageStats.billableMessages} billable</Text>
                </View>
              )}
            </>
          ) : (
            <View style={styles.statItem}>
              <Ionicons name="stats-chart" size={13} color={colors.text.secondary} />
              <Text style={styles.statText}>{successRate}% ({task.executionCount} runs)</Text>
            </View>
          )}
        </View>

        <Text style={styles.taskFooterText}>{formatDate(task.createdAt)}</Text>
      </TouchableOpacity>
    );
  };

  // ─── Access denied ────────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <>
        <ScreenLayout
          header={<Header showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
          showBottomTabs={Platform.OS !== 'web'}
          formHeader={<FormHeader title="Message Task History" onBack={goBack} />}
        >
          <View style={styles.accessDeniedContainer}>
            <Ionicons name="lock-closed" size={64} color={colors.text.secondary} />
            <Text style={styles.accessDeniedTitle}>Access Denied</Text>
            <Text style={styles.accessDeniedText}>You don't have permission to view task history.</Text>
            <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('home')}>
              <Text style={styles.backButtonText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        </ScreenLayout>
        <MoreOptionsModal visible={showMoreModal} onClose={() => setShowMoreModal(false)} />
      </>
    );
  }

  const taskMatchesStatus = (task: Task): boolean => {
    if (statusFilter.size === 0) return true;
    return (
      (statusFilter.has('success') && task.executionCount > 0 && !isTaskEffectivelyFailed(task)) ||
      (statusFilter.has('failed') && isTaskEffectivelyFailed(task)) ||
      (statusFilter.has('pending') && task.status === 1 && !task.lastExecutedAt)
    );
  };

  const taskContactIds = (task: Task): Set<string> => {
    const fromParams: string[] = (task.parameters?.contacts || []).map((c: any) => c.contactId).filter(Boolean);
    const stats = taskMessageStats[task._id];
    const fromStats: string[] = stats?.contactsByStatus
      ? Object.values(stats.contactsByStatus).flatMap(arr => (arr || []).map(c => c.contactId))
      : [];
    return new Set([...fromParams, ...fromStats]);
  };

  const dateFromMs = dateFrom ? dateFrom.getTime() : null;
  const dateToMs = dateTo ? dateTo.getTime() : null;

  const filteredTasks = tasks.filter(task => {
    if (!taskMatchesStatus(task)) return false;
    if (createdByFilter.size > 0 && !(task.createdBy && createdByFilter.has(task.createdBy))) return false;
    if (contactFilter.size > 0) {
      const ids = taskContactIds(task);
      const hasAny = Array.from(contactFilter).some(id => ids.has(id));
      if (!hasAny) return false;
    }
    const createdAtMs = new Date(task.createdAt).getTime();
    if (dateFromMs !== null && createdAtMs < dateFromMs) return false;
    if (dateToMs !== null && createdAtMs > dateToMs + 24 * 60 * 60 * 1000 - 1) return false;
    return true;
  });

  const allTargetedContacts = React.useMemo(() => {
    const seen = new Map<string, { contactId: string; name?: string; phoneNumber?: string }>();
    tasks.forEach(task => {
      (task.parameters?.contacts || []).forEach((c: any) => {
        if (c.contactId && !seen.has(c.contactId)) {
          seen.set(c.contactId, { contactId: c.contactId, name: c.name, phoneNumber: c.phoneNumber || c.phone });
        }
      });
      const stats = taskMessageStats[task._id];
      if (stats?.contactsByStatus) {
        Object.values(stats.contactsByStatus).forEach(arr => (arr || []).forEach(c => {
          if (!seen.has(c.contactId)) seen.set(c.contactId, c);
        }));
      }
    });
    return Array.from(seen.values());
  }, [tasks, taskMessageStats]);

  const filteredContactOptions = contactSearchQuery.trim()
    ? allTargetedContacts.filter(c =>
        (c.name || '').toLowerCase().includes(contactSearchQuery.trim().toLowerCase()) ||
        firstText(c.phoneNumber).includes(contactSearchQuery.trim())
      )
    : allTargetedContacts;

  const activeFilterCount = statusFilter.size + createdByFilter.size + contactFilter.size + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const toggleStatusFilter = (s: 'success' | 'failed' | 'pending') => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  const toggleCreatedByFilter = (userId: string) => {
    setCreatedByFilter(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleContactFilter = (contactId: string) => {
    setContactFilter(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const clearAllFilters = () => {
    setStatusFilter(new Set());
    setCreatedByFilter(new Set());
    setContactFilter(new Set());
    setDateFrom(null);
    setDateTo(null);
  };

  // ─── Reschedule Modal ─────────────────────────────────────────────────────────

  const rescheduleModal = (
    <Modal
      visible={showRescheduleModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowRescheduleModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.rescheduleModal}>
          <Text style={styles.rescheduleTitle}>Reschedule Task</Text>
          <Text style={styles.rescheduleLabel}>New date and time (local):</Text>
          <TextInput
            style={styles.rescheduleInput}
            value={rescheduleDate}
            onChangeText={setRescheduleDate}
            placeholder="YYYY-MM-DDTHH:MM"
            placeholderTextColor={colors.text.secondary}
          />
          <Text style={styles.rescheduleHint}>Example: {new Date(Date.now() + 3600000).toISOString().slice(0, 16)}</Text>
          <View style={styles.rescheduleActions}>
            <TouchableOpacity style={styles.rescheduleCancelBtn} onPress={() => { setShowRescheduleModal(false); setRescheduleDate(''); }}>
              <Text style={styles.rescheduleCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.rescheduleConfirmBtn} onPress={handleReschedule} disabled={actionLoading}>
              {actionLoading
                ? <ActivityIndicator size="small" color={colors.white} />
                : <Text style={styles.rescheduleConfirmText}>Confirm</Text>
              }
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Filter pickers ───────────────────────────────────────────────────────────

  const STATUS_FILTER_OPTIONS: Array<{ key: 'success' | 'failed' | 'pending'; label: string }> = [
    { key: 'success', label: 'Success' },
    { key: 'failed', label: 'Failed' },
    { key: 'pending', label: 'Pending' },
  ];

  const statusPickerModal = (
    <Modal visible={showStatusPicker} transparent animationType="fade" onRequestClose={() => setShowStatusPicker(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.pickerModal}>
          <View style={styles.pickerModalHeader}>
            <Text style={styles.pickerModalTitle}>Filter by status</Text>
            <TouchableOpacity onPress={() => setShowStatusPicker(false)}>
              <Ionicons name="close" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.pickerModalList}>
            {STATUS_FILTER_OPTIONS.map(opt => {
              const checked = statusFilter.has(opt.key);
              return (
                <TouchableOpacity key={opt.key} style={styles.pickerRow} onPress={() => toggleStatusFilter(opt.key)}>
                  <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                    {checked && <Ionicons name="checkmark" size={12} color={colors.white} />}
                  </View>
                  <Text style={styles.pickerRowText}>{opt.label}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <TouchableOpacity style={styles.pickerDoneBtn} onPress={() => setShowStatusPicker(false)}>
            <Text style={styles.pickerDoneBtnText}>Done</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const createdByPickerModal = (
    <Modal visible={showCreatedByPicker} transparent animationType="fade" onRequestClose={() => setShowCreatedByPicker(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.pickerModal}>
          <View style={styles.pickerModalHeader}>
            <Text style={styles.pickerModalTitle}>Filter by sender</Text>
            <TouchableOpacity onPress={() => setShowCreatedByPicker(false)}>
              <Ionicons name="close" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.pickerModalList}>
            {businessUsers.length === 0 ? (
              <Text style={styles.pickerEmptyText}>No users found.</Text>
            ) : businessUsers.map(u => {
              const checked = createdByFilter.has(u.id);
              return (
                <TouchableOpacity key={u.id} style={styles.pickerRow} onPress={() => toggleCreatedByFilter(u.id)}>
                  <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                    {checked && <Ionicons name="checkmark" size={12} color={colors.white} />}
                  </View>
                  <Text style={styles.pickerRowText}>{u.name}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <TouchableOpacity style={styles.pickerDoneBtn} onPress={() => setShowCreatedByPicker(false)}>
            <Text style={styles.pickerDoneBtnText}>Done</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const contactPickerModal = (
    <Modal visible={showContactPicker} transparent animationType="fade" onRequestClose={() => setShowContactPicker(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.pickerModal}>
          <View style={styles.pickerModalHeader}>
            <Text style={styles.pickerModalTitle}>Filter by contact</Text>
            <TouchableOpacity onPress={() => setShowContactPicker(false)}>
              <Ionicons name="close" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          </View>
          <TextInput
            style={styles.pickerSearchInput}
            value={contactSearchQuery}
            onChangeText={setContactSearchQuery}
            placeholder="Search name or phone..."
            placeholderTextColor={colors.text.secondary}
          />
          <ScrollView style={styles.pickerModalList}>
            {filteredContactOptions.length === 0 ? (
              <Text style={styles.pickerEmptyText}>No contacts found.</Text>
            ) : filteredContactOptions.map(c => {
              const checked = contactFilter.has(c.contactId);
              return (
                <TouchableOpacity key={c.contactId} style={styles.pickerRow} onPress={() => toggleContactFilter(c.contactId)}>
                  <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                    {checked && <Ionicons name="checkmark" size={12} color={colors.white} />}
                  </View>
                  <View>
                    <Text style={styles.pickerRowText}>{c.name || c.phoneNumber || c.contactId}</Text>
                    {c.phoneNumber && c.name && <Text style={styles.pickerRowSubtext}>{c.phoneNumber}</Text>}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
          <TouchableOpacity style={styles.pickerDoneBtn} onPress={() => setShowContactPicker(false)}>
            <Text style={styles.pickerDoneBtnText}>Done</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <>
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={showRightPane && Platform.OS === 'web'}
        onRightPaneClose={handleCloseRightPane}
        rightPaneContent="custom"
        rightPaneCustomContent={
          <ScrollView style={styles.rightPaneScroll} contentContainerStyle={styles.rightPaneScrollContent}>
            {renderRightPaneContent()}
          </ScrollView>
        }
        formHeader={<FormHeader title="Message Task History" onBack={goBack} />}
      >
        <ScrollView
          style={styles.content}
          contentContainerStyle={Platform.OS === 'web' ? styles.webContentContainer : undefined}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {/* Filters */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersScroll}>
            <View style={styles.filtersContainer}>
              <TouchableOpacity
                style={[styles.filterButton, statusFilter.size > 0 && styles.filterButtonActive]}
                onPress={() => setShowStatusPicker(true)}
              >
                <Ionicons name="funnel-outline" size={14} color={statusFilter.size > 0 ? colors.white : colors.text.secondary} />
                <Text style={[styles.filterButtonText, statusFilter.size > 0 && styles.filterButtonTextActive]}>
                  {statusFilter.size > 0 ? `Status (${statusFilter.size})` : 'Status'}
                </Text>
              </TouchableOpacity>

              {!isViewTierOnly && (
                <TouchableOpacity
                  style={[styles.filterButton, createdByFilter.size > 0 && styles.filterButtonActive]}
                  onPress={() => setShowCreatedByPicker(true)}
                >
                  <Ionicons name="person-outline" size={14} color={createdByFilter.size > 0 ? colors.white : colors.text.secondary} />
                  <Text style={[styles.filterButtonText, createdByFilter.size > 0 && styles.filterButtonTextActive]}>
                    {createdByFilter.size > 0 ? `Sent by (${createdByFilter.size})` : 'Sent by'}
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[styles.filterButton, contactFilter.size > 0 && styles.filterButtonActive]}
                onPress={() => setShowContactPicker(true)}
              >
                <Ionicons name="people-outline" size={14} color={contactFilter.size > 0 ? colors.white : colors.text.secondary} />
                <Text style={[styles.filterButtonText, contactFilter.size > 0 && styles.filterButtonTextActive]}>
                  {contactFilter.size > 0 ? `Contact (${contactFilter.size})` : 'Contact'}
                </Text>
              </TouchableOpacity>

              <View style={styles.dateRangeGroup}>
                <View style={styles.dateRangeFieldWrap}>
                  <DatePickerField
                    label=""
                    value={dateFrom}
                    onChange={setDateFrom}
                    placeholder="From"
                    maximumDate={dateTo ?? undefined}
                    compact
                    bare
                  />
                </View>
                <Text style={styles.dateRangeSeparator}>–</Text>
                <View style={styles.dateRangeFieldWrap}>
                  <DatePickerField
                    label=""
                    value={dateTo}
                    onChange={setDateTo}
                    placeholder="To"
                    minimumDate={dateFrom ?? undefined}
                    compact
                    bare
                  />
                </View>
              </View>

              {activeFilterCount > 0 && (
                <TouchableOpacity style={styles.filterButton} onPress={clearAllFilters}>
                  <Ionicons name="close-circle-outline" size={14} color={colors.text.secondary} />
                  <Text style={styles.filterButtonText}>Clear</Text>
                </TouchableOpacity>
              )}

              {canResend && (
                <TouchableOpacity
                  style={[styles.filterButton, selectionMode && styles.filterButtonActive]}
                  onPress={() => {
                    setSelectionMode(prev => !prev);
                    setSelectedTaskIds(new Set());
                  }}
                >
                  <Ionicons name={selectionMode ? 'close' : 'checkbox-outline'} size={14} color={selectionMode ? colors.white : colors.text.secondary} />
                  <Text style={[styles.filterButtonText, selectionMode && styles.filterButtonTextActive]}>
                    {selectionMode ? 'Cancel' : 'Select'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>

          {selectionMode && (
            <View style={styles.bulkResendBar}>
              <Text style={styles.bulkResendCountText}>{selectedTaskIds.size} selected</Text>
              <TouchableOpacity
                style={[styles.bulkResendBtn, selectedTaskIds.size === 0 && styles.bulkResendBtnDisabled]}
                onPress={handleBulkResend}
                disabled={selectedTaskIds.size === 0 || bulkResending}
              >
                {bulkResending
                  ? <ActivityIndicator size="small" color={colors.white} />
                  : <Ionicons name="send-outline" size={16} color={colors.white} />
                }
                <Text style={styles.bulkResendBtnText}>Resend Selected</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Tasks List */}
          {loading && tasks.length === 0 ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>Loading tasks...</Text>
            </View>
          ) : filteredTasks.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="document-outline" size={48} color={colors.text.secondary} />
              <Text style={styles.emptyTitle}>No Tasks Found</Text>
              <Text style={styles.emptySubtitle}>
                {activeFilterCount === 0
                  ? 'No WhatsApp message tasks found yet.'
                  : 'No tasks match the current filters.'}
              </Text>
            </View>
          ) : (
            <View style={styles.tasksList}>
              {filteredTasks.map(task => (
                <View key={task._id}>{renderTaskCard({ item: task })}</View>
              ))}
            </View>
          )}
        </ScrollView>

        {/* Execution History Modal for Mobile */}
        {Platform.OS !== 'web' && (
          <Modal
            visible={showExecutionModal && !!selectedTask}
            animationType="slide"
            transparent
            onRequestClose={handleCloseRightPane}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.bottomSheet}>
                <View style={styles.bottomSheetHeader}>
                  <Text style={styles.bottomSheetTitle} numberOfLines={1}>
                    {selectedTask?.name || 'Task Details'}
                  </Text>
                  <TouchableOpacity onPress={handleCloseRightPane} style={styles.closeButton}>
                    <Ionicons name="close" size={24} color={colors.text.primary} />
                  </TouchableOpacity>
                </View>
                <ScrollView style={styles.bottomSheetBody} contentContainerStyle={styles.bottomSheetBodyContent}>
                  {selectedTask ? renderRightPaneContent() : (
                    <View style={styles.loadingContainer}>
                      <ActivityIndicator size="large" color={colors.primary} />
                    </View>
                  )}
                </ScrollView>
              </View>
            </View>
          </Modal>
        )}
      </ScreenLayout>

      {rescheduleModal}
      {statusPickerModal}
      {createdByPickerModal}
      {contactPickerModal}
      <MoreOptionsModal visible={showMoreModal} onClose={() => setShowMoreModal(false)} />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  content: { flex: 1, backgroundColor: colors.background.primary },
  webContentContainer: { paddingTop: spacing.xl },
  filtersScroll: { flexGrow: 0 },
  filtersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.xs,
  },
  dateRangeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  dateRangeFieldWrap: { minWidth: 96 },
  dateRangeSeparator: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  pickerModal: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    marginHorizontal: spacing.lg,
    maxHeight: '70%',
    width: Platform.OS === 'web' ? 420 : undefined,
    alignSelf: 'center',
    padding: spacing.md,
  },
  pickerModalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  pickerModalTitle: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  pickerSearchInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  pickerModalList: { maxHeight: 320 },
  pickerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  pickerRowText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  pickerRowSubtext: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  pickerEmptyText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, textAlign: 'center', paddingVertical: spacing.lg },
  pickerDoneBtn: { marginTop: spacing.sm, backgroundColor: colors.primary, borderRadius: spacing.borderRadius.md, paddingVertical: spacing.sm, alignItems: 'center' },
  pickerDoneBtnText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  filterButtonActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  filterButtonText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  filterButtonTextActive: { color: colors.white },

  bulkResendBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
  },
  bulkResendCountText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  bulkResendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.md,
  },
  bulkResendBtnDisabled: { opacity: 0.5 },
  bulkResendBtnText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.white },

  // Task card
  tasksList: { padding: spacing.md, gap: spacing.md },
  taskCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  taskCardScheduled: { borderLeftWidth: 4, borderLeftColor: colors.warning },
  taskCardSelected: { borderColor: colors.primary, borderWidth: 2 },
  taskCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm },
  taskCardTitleRow: { flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: spacing.sm, gap: spacing.xs },
  taskCardTitle: { ...typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, flex: 1 },
  statusBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: spacing.borderRadius.sm, maxWidth: 140 },
  statusText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.medium },
  taskStats: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.xs },
  statItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  statText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  taskFooterText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },

  // Right pane / detail
  rightPaneScroll: { flex: 1 },
  rightPaneScrollContent: { padding: spacing.md, paddingBottom: spacing.xl * 2 },
  rightPaneEmpty: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, minHeight: 400 },
  rightPaneEmptyTitle: { ...typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, marginTop: spacing.md, marginBottom: spacing.xs },
  rightPaneEmptyText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, textAlign: 'center' },

  detailsHeaderRow: { marginBottom: spacing.lg },
  detailsTitle: { ...typography.fontSize.xl, fontFamily: typography.fontFamily.bold, color: colors.text.primary, marginBottom: spacing.xs },
  detailsSubtitle: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },

  // Section
  section: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md },
  sectionTitle: { ...typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, flex: 1 },

  // Info grid
  infoGrid: { gap: spacing.xs },
  infoItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 3 },
  infoLabel: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary, flex: 1 },
  infoValue: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary, flex: 2, textAlign: 'right' },

  // Stat pills
  pillsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  statPill: {
    flex: 1,
    minWidth: 70,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    backgroundColor: colors.background.primary,
  },
  statPillValue: { ...typography.fontSize.lg, fontFamily: typography.fontFamily.bold },
  statPillLabel: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: 2 },

  // Success rate badge
  successRateBadge: { backgroundColor: `${colors.success}20`, paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: spacing.borderRadius.sm },
  successRateText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.success },

  // Contact review
  statusTabsScroll: { marginBottom: spacing.sm },
  statusTabs: { flexDirection: 'row', gap: spacing.xs, paddingBottom: spacing.xs },
  statusTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.primary,
  },
  statusTabActive: { borderColor: colors.primary, backgroundColor: `${colors.primary}15` },
  statusTabText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  statusTabTextActive: { fontFamily: typography.fontFamily.semibold, color: colors.primary },

  contactActionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    paddingVertical: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  selectAllRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  selectAllText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  sendSelectedBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#25D366',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
  },
  sendSelectedBtnText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.white },

  contactList: { gap: spacing.xs },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: 'transparent',
    backgroundColor: colors.background.primary,
  },
  contactRowSelected: { borderColor: colors.primary, backgroundColor: `${colors.primary}10` },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.surface,
  },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  checkboxDisabled: { opacity: 0.4 },
  contactInfo: { flex: 1 },
  contactName: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  contactPhone: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  contactErrorText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.primary },
  contactErrorMoreText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.primary, marginTop: 2 },
  pausedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F59E0B20',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  pausedBannerText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: '#92400E', flex: 1 },
  contactStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: spacing.borderRadius.sm,
  },
  contactStatusText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.medium },
  emptyContactsText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, textAlign: 'center', padding: spacing.md },

  // Actions
  actionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: `${colors.primary}10`,
  },
  actionBtnPrimary: { backgroundColor: colors.primary, borderColor: colors.primary },
  actionBtnDanger: { borderColor: '#EF4444', backgroundColor: '#EF444410' },
  actionBtnText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.primary },

  // Execution log
  executionLogCard: {
    backgroundColor: colors.background.primary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  executionLogHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 4 },
  executionStatusDot: { width: 10, height: 10, borderRadius: 5 },
  executionStatusText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, flex: 1 },
  retryText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  executionDuration: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  executionLogMeta: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  errorContainer: { marginTop: spacing.xs, padding: spacing.xs, backgroundColor: '#EF444415', borderRadius: spacing.borderRadius.sm, borderLeftWidth: 2, borderLeftColor: '#EF4444' },
  errorText: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.primary },
  emptyExecutionContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center' },
  emptyExecutionText: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: spacing.sm },

  // Loading / empty
  loadingContainer: { padding: spacing.xl, alignItems: 'center', gap: spacing.sm },
  loadingText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  emptyContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center', minHeight: 300 },
  emptyTitle: { ...typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, marginTop: spacing.md, marginBottom: spacing.xs },
  emptySubtitle: { ...typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, textAlign: 'center' },

  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  bottomSheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: spacing.borderRadius.xl,
    borderTopRightRadius: spacing.borderRadius.xl,
    maxHeight: '90%',
    minHeight: '50%',
    paddingBottom: Platform.OS === 'ios' ? spacing.xl : spacing.md,
  },
  bottomSheetHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  bottomSheetTitle: { ...typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary, flex: 1 },
  closeButton: { padding: spacing.xs },
  bottomSheetBody: { flex: 1 },
  bottomSheetBodyContent: { padding: spacing.md },

  // Reschedule modal
  rescheduleModal: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: spacing.borderRadius.xl,
    borderTopRightRadius: spacing.borderRadius.xl,
    padding: spacing.xl,
    paddingBottom: Platform.OS === 'ios' ? spacing.xl * 2 : spacing.xl,
  },
  rescheduleTitle: { ...typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary, marginBottom: spacing.md },
  rescheduleLabel: { ...typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.secondary, marginBottom: spacing.xs },
  rescheduleInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  rescheduleHint: { ...typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginBottom: spacing.lg },
  rescheduleActions: { flexDirection: 'row', gap: spacing.md },
  rescheduleCancelBtn: { flex: 1, paddingVertical: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, alignItems: 'center' },
  rescheduleCancelText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  rescheduleConfirmBtn: { flex: 1, paddingVertical: spacing.md, borderRadius: spacing.borderRadius.md, backgroundColor: colors.primary, alignItems: 'center' },
  rescheduleConfirmText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white },

  // Access denied
  accessDeniedContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  accessDeniedTitle: { ...typography.fontSize.xl, fontFamily: typography.fontFamily.bold, color: colors.text.primary, marginTop: spacing.md, marginBottom: spacing.sm },
  accessDeniedText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, textAlign: 'center', marginBottom: spacing.xl },
  backButton: { backgroundColor: colors.primary, paddingHorizontal: spacing.xl, paddingVertical: spacing.md, borderRadius: spacing.borderRadius.md },
  backButtonText: { ...typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.white },
});
